import React, { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-hot-toast'

import { BaseSafeFormView, BaseView, BaseText } from '@/components/base'
// import { HeaderNavigation } from '@/components/HeaderNavigation'
import { AppButton } from '@/components/AppButton'
import { AnimatedTextInput } from '@/components/AnimatedTextInput'
import { ReportReasonsList } from '@/components/ReportReasonsList'
import { reasonsKey } from '@/components/ReportReasonsList/reasonsList'
import { useReportUser } from '@/domain/identity'
import useOffsets from '@/hooks/useOffsets'
import { StickyHeaderNavigation } from '@/components/StickyHeaderNavigation'

interface ReportUserParams {
  userId: string
}

export const ReportUserScreen: React.FC = () => {
  const [message, setMessage] = useState('')
  const [selectedReasons, setSelectedReasons] = useState<reasonsKey[]>([])

  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const offsets = useOffsets()

  // Get userId from location state or URL params
  const locationState = location.state as ReportUserParams | null
  const searchParams = new URLSearchParams(location.search)
  const userId = locationState?.userId || searchParams.get('userId') || ''

  const { mutateAsync: reportUser, isPending: isReportingUser } = useReportUser(
    {
      onSuccess: () => {
        toast.success(t('screens:reportUser.reportSubmitted'))
        navigate(-1) // Go back to previous screen
      },
      onError: ({ errorMessage }) => {
        if (errorMessage) {
          toast.error(errorMessage)
        }
      },
    },
  )

  const handleSubmitReport = async () => {
    if (!userId) {
      toast.error(t('common:userNotFound'))
      return
    }

    if (!message.trim() && selectedReasons.length === 0) {
      toast.error(t('screens:reportUser.pleaseProvideReason'))
      return
    }

    try {
      await reportUser({
        userId,
        message: `${selectedReasons.join(', ')} ${message}`.trim(),
      })
    } catch (error) {
      // Error handled by onError callback
    }
  }

  if (!userId) {
    return (
      <BaseSafeFormView flex={1} bgColor="background.screen">
        <StickyHeaderNavigation />
        <BaseView flex={1} center px={20}>
          <BaseText
            fontSize={18}
            fontFamily="medium"
            color="label.default"
            textAlign="center"
            className="mb-4"
          >
            {t('common:userNotFound')}
          </BaseText>
        </BaseView>
      </BaseSafeFormView>
    )
  }

  return (
    <BaseView
      style={{
        flexDirection: 'column',
        justifyContent: 'space-between',
        height: '100vh',
        paddingTop: `${offsets.top}px`,
      }}
    >
      <StickyHeaderNavigation />
      <BaseView
        style={{
          paddingTop: `${offsets.top - 30}px`,
        }}
        px={13}
        flex={1}
        bgColor="background.screen"
      >
        <BaseView style={{ gap: '28px', justifyContent: 'flex-end' }}>
          <BaseView style={{ gap: '12px' }}>
            <BaseText fontSize={28} fontFamily="semibold" color="label.default">
              {t('translation:reasonForReporting')}
            </BaseText>

            <ReportReasonsList
              selectedReasons={selectedReasons}
              setReasons={setSelectedReasons}
            />
          </BaseView>

          <BaseView style={{ gap: '12px' }}>
            <BaseText fontSize={18} fontFamily="bold" color="label.default">
              {t('translation:message')}
            </BaseText>
            <AnimatedTextInput
              isTextArea
              label={t('translation:whatsHappenedMessage')}
              value={message}
              onChangeText={setMessage}
              multiple
              maxLength={150}
              returnKeyType="done"
            />
          </BaseView>
        </BaseView>
      </BaseView>

      <BaseView
        // px={18}
        px={13}
        style={{
          paddingTop: '12px',
          paddingBottom: `${offsets.bottom}px`,
        }}
      >
        <AppButton
          label={t('translation:submit')}
          disabled={!selectedReasons.length}
          isLoading={isReportingUser}
          onPress={handleSubmitReport}
        />
      </BaseView>
    </BaseView>
  )
}
